import { useEffect, useState } from 'react';
import { apiFetch, useAuthStore } from '@/stores/authStore';

/**
 * Penelope's three locks, as GET /api/trader/me resolved them (`penelopeAccess`, `penelopeLiveAccess`,
 * `penelopeAdvancedAccess`). They fail OPEN on the relay: everyone has Penelope, and a manager switches a trader
 * off by storing `false` on traderRules.penelopeEnabled / penelopeLiveEnabled / penelopeAdvancedEnabled. The
 * relay always sends resolved booleans, so the store's `?? false` only covers a /me that predates the field.
 * Display gating only — the relay re-resolves the locks on every /api/penelope request.
 */

/** Not switched off: may attach, arm and run their own games (any role; orders use the caller's own limits). */
export function usePenelopeAccess(): boolean {
  return useAuthStore((s) => s.user?.penelopeAccess ?? false);
}

/** Not locked to practice: may run a game in mode 'live' — the box must allow live as well (by-event `liveEligible`). */
export function usePenelopeLiveAccess(): boolean {
  return useAuthStore((s) => (s.user?.penelopeAccess ?? false) && (s.user?.penelopeLiveAccess ?? false));
}

/** Sees the advanced controls (knob groups, variants, clipboard, research panel). Presentation only: the relay is
 *  tier-blind, so this never guards a write. Operators are not implied here — callers OR in `role` where wanted. */
export function usePenelopeAdvancedAccess(): boolean {
  return useAuthStore((s) => (s.user?.penelopeAccess ?? false) && (s.user?.penelopeAdvancedAccess ?? false));
}

/** How long one answer from /api/penelope/status serves every mounted caller. */
const SERVICE_ON_TTL_MS = 60_000;
let serviceOnCache: { on: boolean; atMs: number } | null = null;
let serviceOnInflight: Promise<boolean> | null = null;

/** One shared read of whether this box runs Penelope at all (`health.enabled` = PENELOPE=1), cached 60 s. */
function readPenelopeServiceOn(): Promise<boolean> {
  if (serviceOnCache && Date.now() - serviceOnCache.atMs < SERVICE_ON_TTL_MS) return Promise.resolve(serviceOnCache.on);
  if (serviceOnInflight) return serviceOnInflight;
  serviceOnInflight = (async () => {
    try {
      const res = await apiFetch('/api/penelope/status');
      if (!res.ok) return false;
      const body = (await res.json()) as { health?: { enabled?: unknown } };
      return body.health?.enabled === true;
    } catch {
      return false;
    }
  })()
    .then((on) => { serviceOnCache = { on, atMs: Date.now() }; return on; })
    .finally(() => { serviceOnInflight = null; });
  return serviceOnInflight;
}

/** Test seam: forget the cached answer. */
export function resetPenelopeServiceOnCache(): void {
  serviceOnCache = null;
  serviceOnInflight = null;
}

/**
 * Whether this box runs Penelope at all (GET /api/penelope/status → `health.enabled`, i.e. PENELOPE=1), so the
 * ticket can hide the tab on a box without the service instead of offering a dead one. Module-cached for 60 s
 * across every caller; `enabled=false` (no access) skips the read and answers false. Fails closed on any error.
 */
export function usePenelopeServiceOn(enabled = true): boolean {
  const [on, setOn] = useState<boolean>(() => (enabled && serviceOnCache?.on) ?? false);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    void readPenelopeServiceOn().then((v) => { if (alive) setOn(v); });
    return () => { alive = false; };
  }, [enabled]);
  return enabled && on;
}
